import React from 'react';
import { Link } from 'react-router-dom';
import { useSite } from '../content/SiteContext';
import { businessSchema, serviceSchema, faqSchema } from '../lib/schema';
import { whatsappLink } from '../lib/contact';
import { PageMeta, PageHero, SectionHead, PackageCard, CtaBand, FaqList, Icon, AppButton, StoreBadge } from '../components/ui';
import NotFound from './NotFound';

const CityPage = ({ cityId }) => {
  const { getCity, CITIES, PACKAGES, ADDONS, PLANS, SERVICE_PAGES } = useSite();
  const city = getCity(cityId);
  // The area may be unpublished in the CMS — treat it as not served.
  if (!city) return <NotFound />;
  const others = CITIES.filter((c) => c.id !== cityId);
  const priceList = PACKAGES.map((p) => `${p.name} AED ${p.price}`).join(', ');
  const minPlan = PLANS.length ? Math.min(...PLANS.map((p) => p.price)) : null;
  const ORDER_STEPS = [
    { icon: 'play', title: 'Download the Quick Way app', text: 'Install the free Quick Way app from Google Play and add your car details.' },
    { icon: 'pin', title: `Pin your location in ${city.name}`, text: 'Drop a pin where the car is parked and add the building, parking level and bay or your villa gate details.' },
    { icon: 'calendar', title: 'Choose a package, date and time', text: `Pick the Basic Wash, Deluxe Wash or Premium Detail, add any extras and choose when suits you.` },
    { icon: 'sparkle', title: 'We wash your car where it is parked', text: 'Our team confirms your slot and final price, then cleans your car at your home, building or office.' }
  ];
  const faqs = [
    { q: `How much is a mobile car wash in ${city.name}?`, a: `${priceList}. Extras such as odour removal or ceramic coating are priced separately. The final price, including any applicable VAT, is confirmed before the wash.` },
    { q: `Which areas of ${city.name} do you cover?`, a: `We regularly serve ${(city.areas || []).join(', ')}. For other parts of ${city.name}, share your location when you book and we will confirm whether we can reach you.` },
    { q: `How do I book a car wash at home in ${city.name}?`, a: 'Order in the Quick Way Android app: add your car, pin your location, choose a package and time. You can also use our booking form or message us on WhatsApp. Your booking is confirmed when our team replies with your slot.' },
    { q: 'How long does a car wash take?', a: PACKAGES.map((p) => `${p.name}: ${p.duration.toLowerCase()}`).join('. ') + '. Larger or very dirty vehicles can take longer.' },
    { q: 'Can you wash my car in my building car park?', a: 'Often, yes. Some buildings restrict car washing in their car parks, so check with your building management and share the building name, parking level and bay when booking.' },
    { q: `Can I get my car washed at the office in ${city.name}?`, a: 'Yes. Share the car park name, level and bay plus any visitor-access steps so the team can reach your car while you work.' },
    { q: 'Do prices differ for SUVs and larger vehicles?', a: 'The prices shown are our standard package prices. For large SUVs, vans or heavily soiled vehicles, we confirm the final price with you before the wash starts.' },
    ...(minPlan ? [{ q: `Do you offer monthly car wash plans in ${city.name}?`, a: `Yes. Monthly plans start from AED ${minPlan} and include priority booking. Plan terms are confirmed with you before you subscribe.` }] : [])
  ];

  return (
    <>
      <PageMeta
        title={city.title}
        description={city.description}
        path={city.path}
        crumbs={[{ name: 'Locations', path: '/locations' }, { name: city.name, path: city.path }]}
        jsonLd={[
          businessSchema([city.id]),
          serviceSchema({ name: `Mobile car wash in ${city.name}`, description: city.description, path: city.path, offers: PACKAGES }),
          faqSchema(faqs)
        ]}
      />
      <PageHero eyebrow={`Service area · ${city.name}`} title={city.h1}
        actions={<>
          <AppButton label="Book in the app" className="btn btn-primary btn-lg" source={`city_${city.id}`} />
          <a href={whatsappLink(`Hi Quick Way, I would like to book a car wash in ${city.name}.`)} target="_blank" rel="noopener noreferrer" className="btn btn-ghost btn-lg">WhatsApp us</a>
        </>}>
        <p>{city.intro}</p>
      </PageHero>

      <section className="section">
        <div className="container">
          <SectionHead title={`Car wash packages in ${city.name}`}>
            The same clear prices across {city.name}, whether we wash at your villa, building or office.
          </SectionHead>
          <div className="grid grid-3">
            {PACKAGES.map((p) => <PackageCard key={p.id} pkg={p} />)}
          </div>
        </div>
      </section>

      <section className="section section--tint">
        <div className="container container--narrow">
          <h2 className="h-section">Car wash prices in {city.name}</h2>
          <div className="table-wrap" role="region" aria-label={`Car wash prices in ${city.name}`} tabIndex="0">
            <table className="simple-table">
              <thead><tr><th scope="col">Package</th><th scope="col">What is included</th><th scope="col">Time</th><th scope="col">Price</th></tr></thead>
              <tbody>
                {PACKAGES.map((p) => (
                  <tr key={p.id}>
                    <th scope="row"><Link to={p.servicePage}>{p.name}</Link></th>
                    <td>{p.includes.join(', ')}</td>
                    <td>{p.duration}</td>
                    <td>AED {p.price}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {ADDONS.length > 0 && (
            <p className="muted">
              Extras: {ADDONS.map((a) => `${a.name.toLowerCase()} (AED ${a.price})`).join(', ')}.{' '}
              See <Link to="/pricing">all car wash prices</Link> or save with <Link to="/membership">monthly plans</Link>.
            </p>
          )}
        </div>
      </section>

      <section className="section">
        <div className="container">
          <SectionHead title={`How to order a car wash in ${city.name}`}>
            The quickest way to book is the Quick Way app for Android.
          </SectionHead>
          <ol className="steps">
            {ORDER_STEPS.map((s, i) => (
              <li key={s.title} className="step">
                <span className="step__num">{i + 1}</span>
                <span className="icon-tile"><Icon name={s.icon} /></span>
                <h3>{s.title}</h3>
                <p>{s.text}</p>
              </li>
            ))}
          </ol>
          <p className="center-note">
            <StoreBadge source={`city_${city.id}_steps`} />
            <br />
            <Link to="/car-wash-app" className="link-arrow">More about the Quick Way app <Icon name="arrow" size={16} /></Link>
          </p>
        </div>
      </section>

      <section className="section section--tint">
        <div className="container">
          <SectionHead title={`Car wash at home in ${city.name}: what to know`} />
          <div className="grid grid-3">
            {(city.local || []).map((l) => (
              <div key={l.h} className="feature">
                <h3>{l.h}</h3>
                <p>{l.p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container split split--top">
          <div>
            <h2 className="h-section">Areas we serve in {city.name}</h2>
            <ul className="area-list area-list--lg">
              {(city.areas || []).map((a) => <li key={a}><Icon name="pin" size={16} /> {a}</li>)}
            </ul>
            <p className="muted">Not listed? Send your location when booking and we will confirm coverage. <Link to="/locations">See all service areas</Link>.</p>
          </div>
          <div>
            <h2 className="h-section">Services available in {city.name}</h2>
            <div className="related">
              {SERVICE_PAGES.map((s) => (
                <Link key={s.slug} to={`/services/${s.slug}`} className="related__item">
                  <span>{s.name} in {city.name}</span><Icon name="arrow" size={16} />
                </Link>
              ))}
              <Link to="/membership" className="related__item"><span>Monthly car wash plans</span><Icon name="arrow" size={16} /></Link>
              <Link to="/corporate-fleet-car-wash" className="related__item"><span>Fleet car wash in {city.name}</span><Icon name="arrow" size={16} /></Link>
            </div>
          </div>
        </div>
      </section>

      <section className="section section--tint">
        <div className="container container--narrow">
          <SectionHead title={`Mobile car wash in ${city.name}: FAQ`} />
          <FaqList items={faqs} />
          {others.length > 0 && (
            <p className="center-note">
              Also available: {others.map((o, i) => <React.Fragment key={o.id}>{i > 0 && ', '}<Link to={o.path}>mobile car wash in {o.name}</Link></React.Fragment>)}.
            </p>
          )}
        </div>
      </section>

      <CtaBand title={`Book a car wash in ${city.name}`} source={`city_${city.id}`} />
    </>
  );
};

export default CityPage;
